const MINUTES_PER_DAY = 24 * 60;

/**
 * Offset en minutos entre una timezone y UTC, vigente en la fecha actual.
 *
 * Los horarios de trabajo se guardan como hora UTC "plana" (sin fecha). Tanto al guardar
 * (`localTimeToUTC*`) como al leer (`utcTimeToLocal*`) hay que usar EXACTAMENTE el mismo offset:
 * si uno usa el de hoy y el otro el de otra época del año, en zonas con horario de verano
 * (ej. Chile: UTC-3 en verano, UTC-4 en invierno) lo que se ve en la agenda se corre una hora
 * respecto de lo que la psicóloga configuró.
 */
function getTimezoneOffsetMinutes(timezone: string): number {
  const now = new Date();
  // Mediodía UTC de hoy: lejos de cualquier borde de día en todas las zonas.
  const reference = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0));

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(reference);

  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  const localAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));

  return Math.round((localAsUtc - reference.getTime()) / 60000);
}

function shiftTime(timeStr: string, offsetMinutes: number): { time: string; dayShift: number } {
  const [hours = 0, minutes = 0, seconds = 0] = timeStr.split(':').map(Number);

  const total = hours * 60 + minutes + offsetMinutes;
  const dayShift = Math.floor(total / MINUTES_PER_DAY);
  const wrapped = ((total % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  const hh = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const mm = String(wrapped % 60).padStart(2, '0');
  const ss = String(seconds || 0).padStart(2, '0');

  return { time: `${hh}:${mm}:${ss}`, dayShift };
}

export function localTimeToUTC(timeStr: string, timezone: string): string {
  return localTimeToUTCWithDayShift(timeStr, timezone).time;
}

/**
 * Convierte hora local a UTC y retorna cuántos días cambia (0, +1, -1)
 */
export function localTimeToUTCWithDayShift(timeStr: string, timezone: string): { time: string; dayShift: number } {
  return shiftTime(timeStr, -getTimezoneOffsetMinutes(timezone));
}

export function utcTimeToLocal(timeStr: string, timezone: string): string {
  return utcTimeToLocalWithDayShift(timeStr, timezone).time;
}

/**
 * Convierte hora UTC a hora local y retorna cuántos días cambia (0, +1, -1)
 * Ej: 00:00 UTC en Argentina (UTC-3) → 21:00 del día anterior, dayShift = -1
 */
export function utcTimeToLocalWithDayShift(timeStr: string, timezone: string): { time: string; dayShift: number } {
  if (!timeStr) return { time: '', dayShift: 0 };
  return shiftTime(timeStr, getTimezoneOffsetMinutes(timezone));
}
